import { useState, useEffect } from "react";
import { AdminLayout } from "./AdminDashboard";
import API from "../../lib/api";
import { toast } from "sonner";
import { Shield, Save, AlertTriangle, RefreshCw } from "lucide-react";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";

const LOG_TYPE_LABELS = {
  multi_account_ip: "Multi-Account (IP)",
  multi_account_fingerprint: "Multi-Account (Fingerprint)",
  suspicious_registration: "Suspicious Registration",
  ip_reward_limit: "IP Reward Limit",
  admin_mark_suspicious: "Admin: Mark Suspicious",
  admin_unmark_suspicious: "Admin: Unmark Suspicious",
  admin_force_verify: "Admin: Force Verify",
  admin_reset_ip_rewards: "Admin: Reset IP Rewards",
  admin_bulk_delete_ip: "Admin: Bulk Delete IP",
};

const LOG_TYPE_COLORS = {
  multi_account_ip: "#EF4444",
  multi_account_fingerprint: "#F59E0B",
  suspicious_registration: "#EF4444",
  ip_reward_limit: "#F59E0B",
  admin_mark_suspicious: "#3B82F6",
  admin_unmark_suspicious: "#10B981",
  admin_force_verify: "#10B981",
  admin_reset_ip_rewards: "#B026FF",
  admin_bulk_delete_ip: "#EF4444",
};

export default function AdminAntiAbuse() {
  const [settings, setSettings] = useState(null);
  const [logs, setLogs] = useState([]);
  const [logFilter, setLogFilter] = useState("");
  const [saving, setSaving] = useState(false);
  const [tab, setTab] = useState("settings");

  useEffect(() => {
    loadSettings();
    loadLogs();
  }, []);

  const loadSettings = async () => {
    try {
      const { data } = await API.get("/admin/anti-abuse/settings");
      setSettings(data);
    } catch { toast.error("Failed to load settings"); }
  };

  const loadLogs = async (type = "") => {
    try {
      const url = type ? `/admin/anti-abuse/logs?type=${type}` : "/admin/anti-abuse/logs";
      const { data } = await API.get(url);
      setLogs(data.logs || []);
    } catch { toast.error("Failed to load logs"); }
  };

  const saveSettings = async () => {
    setSaving(true);
    try {
      await API.put("/admin/anti-abuse/settings", settings);
      toast.success("Anti-abuse settings saved");
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setSaving(false); }
  };

  const handleFilterLogs = (type) => {
    setLogFilter(type);
    loadLogs(type);
  };

  if (!settings) return <AdminLayout title="Anti-Abuse"><p className="text-[#94A3B8]">Loading...</p></AdminLayout>;

  return (
    <AdminLayout title="Anti-Abuse System">
      <div className="space-y-6" data-testid="admin-anti-abuse">
        {/* Tab Switcher */}
        <div className="flex gap-1 bg-[#131722] rounded-lg p-1 border border-[#2A3241]/40">
          <button onClick={() => setTab("settings")} data-testid="tab-settings"
            className={`flex-1 py-2.5 text-xs font-bold uppercase tracking-widest rounded-md transition-all ${tab === "settings" ? "bg-[#EF4444]/10 text-[#EF4444]" : "text-[#64748B] hover:text-[#94A3B8]"}`}>
            Settings
          </button>
          <button onClick={() => setTab("logs")} data-testid="tab-logs"
            className={`flex-1 py-2.5 text-xs font-bold uppercase tracking-widest rounded-md transition-all ${tab === "logs" ? "bg-[#EF4444]/10 text-[#EF4444]" : "text-[#64748B] hover:text-[#94A3B8]"}`}>
            Activity Logs ({logs.length})
          </button>
        </div>

        {tab === "settings" && (
          <div className="bg-[#131722] rounded-xl border border-[#2A3241]/40 p-6 space-y-6">
            <div className="flex items-center gap-3 mb-2">
              <Shield className="w-5 h-5 text-[#EF4444]" />
              <h2 className="text-lg font-bold text-[#F8FAFC]">Global Anti-Abuse Settings</h2>
            </div>

            {/* IP Limiting */}
            <div className="bg-[#0B0E14] rounded-lg p-4 space-y-3">
              <h3 className="text-sm font-bold text-[#00F0FF] uppercase tracking-wider">IP Limiting</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label className="text-[#94A3B8] text-xs">Max Accounts per IP</Label>
                  <Input type="number" min="1" value={settings.max_accounts_per_ip}
                    onChange={e => setSettings({...settings, max_accounts_per_ip: parseInt(e.target.value) || 3})}
                    className="bg-[#131722] border-[#2A3241] text-[#F8FAFC] h-9 mt-1"
                    data-testid="input-max-accounts-per-ip" />
                  <p className="text-[10px] text-[#64748B] mt-1">Accounts beyond this are flagged suspicious (not blocked)</p>
                </div>
                <div>
                  <Label className="text-[#94A3B8] text-xs">Max Rewards per IP per Day</Label>
                  <Input type="number" min="0" step="0.1" value={settings.max_rewards_per_ip_per_day}
                    onChange={e => setSettings({...settings, max_rewards_per_ip_per_day: parseFloat(e.target.value) || 0})}
                    className="bg-[#131722] border-[#2A3241] text-[#F8FAFC] h-9 mt-1"
                    data-testid="input-max-rewards-per-ip" />
                  <p className="text-[10px] text-[#64748B] mt-1">0 = unlimited. Blocks rewards from IP after this limit</p>
                </div>
              </div>
            </div>

            {/* New Account Cooldown */}
            <div className="bg-[#0B0E14] rounded-lg p-4 space-y-3">
              <h3 className="text-sm font-bold text-[#FFD600] uppercase tracking-wider">New Account Cooldown</h3>
              <div>
                <Label className="text-[#94A3B8] text-xs">Cooldown Duration (hours)</Label>
                <Input type="number" min="0" max="168" value={settings.cooldown_hours}
                  onChange={e => setSettings({...settings, cooldown_hours: parseInt(e.target.value) || 0})}
                  className="bg-[#131722] border-[#2A3241] text-[#F8FAFC] h-9 mt-1 w-48"
                  data-testid="input-cooldown-hours" />
                <p className="text-[10px] text-[#64748B] mt-1">New accounts can't withdraw or earn large rewards during this period. 0 = disabled</p>
              </div>
            </div>

            {/* Fingerprint Detection */}
            <div className="bg-[#0B0E14] rounded-lg p-4 space-y-3">
              <h3 className="text-sm font-bold text-[#B026FF] uppercase tracking-wider">Device Fingerprinting</h3>
              <label className="flex items-center gap-3 cursor-pointer" data-testid="toggle-fingerprint">
                <input type="checkbox" checked={settings.fingerprint_enabled}
                  onChange={e => setSettings({...settings, fingerprint_enabled: e.target.checked})}
                  className="w-4 h-4 accent-[#B026FF]" />
                <span className="text-sm text-[#F8FAFC]">Enable device fingerprint detection</span>
              </label>
              <p className="text-[10px] text-[#64748B]">Tracks IP + User Agent + Screen Resolution to detect shared devices</p>
            </div>

            {/* CAPTCHA */}
            <div className="bg-[#0B0E14] rounded-lg p-4 space-y-3">
              <h3 className="text-sm font-bold text-[#10B981] uppercase tracking-wider">CAPTCHA (Cloudflare Turnstile)</h3>
              <label className="flex items-center gap-3 cursor-pointer" data-testid="toggle-captcha">
                <input type="checkbox" checked={settings.captcha_enabled}
                  onChange={e => setSettings({...settings, captcha_enabled: e.target.checked})}
                  className="w-4 h-4 accent-[#10B981]" />
                <span className="text-sm text-[#F8FAFC]">Enable CAPTCHA on registration</span>
              </label>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-2">
                <div>
                  <Label className="text-[#94A3B8] text-xs">Site Key</Label>
                  <Input value={settings.captcha_site_key}
                    onChange={e => setSettings({...settings, captcha_site_key: e.target.value})}
                    placeholder="0x4AAAAAAA..."
                    className="bg-[#131722] border-[#2A3241] text-[#F8FAFC] h-9 mt-1 font-mono text-xs"
                    data-testid="input-captcha-site-key" />
                </div>
                <div>
                  <Label className="text-[#94A3B8] text-xs">Secret Key</Label>
                  <Input type="password" value={settings.captcha_secret_key}
                    onChange={e => setSettings({...settings, captcha_secret_key: e.target.value})}
                    placeholder="0x4AAAAAAA..."
                    className="bg-[#131722] border-[#2A3241] text-[#F8FAFC] h-9 mt-1 font-mono text-xs"
                    data-testid="input-captcha-secret-key" />
                </div>
              </div>
              <p className="text-[10px] text-[#64748B]">Get keys from dash.cloudflare.com → Turnstile</p>
            </div>

            <button onClick={saveSettings} disabled={saving} data-testid="save-anti-abuse-btn"
              className="px-6 py-3 text-sm font-bold uppercase tracking-widest bg-[#EF4444] text-white rounded-lg hover:bg-[#EF4444]/90 disabled:opacity-50 flex items-center gap-2">
              <Save className="w-4 h-4" /> {saving ? "Saving..." : "Save Settings"}
            </button>
          </div>
        )}

        {tab === "logs" && (
          <div className="space-y-4">
            {/* Filters */}
            <div className="flex flex-wrap gap-2">
              <button onClick={() => handleFilterLogs("")} data-testid="filter-all"
                className={`px-3 py-1.5 text-xs rounded-lg border transition-all ${!logFilter ? "bg-[#EF4444]/10 text-[#EF4444] border-[#EF4444]/30" : "text-[#64748B] border-[#2A3241] hover:text-[#F8FAFC]"}`}>
                All
              </button>
              {Object.entries(LOG_TYPE_LABELS).map(([key, label]) => (
                <button key={key} onClick={() => handleFilterLogs(key)} data-testid={`filter-${key}`}
                  className={`px-3 py-1.5 text-xs rounded-lg border transition-all ${logFilter === key ? "bg-[#EF4444]/10 text-[#EF4444] border-[#EF4444]/30" : "text-[#64748B] border-[#2A3241] hover:text-[#F8FAFC]"}`}>
                  {label}
                </button>
              ))}
              <button onClick={() => loadLogs(logFilter)} className="px-3 py-1.5 text-xs text-[#94A3B8] hover:text-[#F8FAFC] ml-auto" data-testid="refresh-logs">
                <RefreshCw className="w-3.5 h-3.5" />
              </button>
            </div>

            {/* Logs List */}
            <div className="space-y-2">
              {logs.length === 0 && <p className="text-[#64748B] text-sm text-center py-8">No activity logs yet</p>}
              {logs.map((log, i) => (
                <div key={i} className="bg-[#131722] rounded-lg border border-[#2A3241]/40 p-3 flex items-start gap-3" data-testid={`log-${i}`}>
                  <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" style={{ color: LOG_TYPE_COLORS[log.type] || "#64748B" }} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-[10px] px-1.5 py-0.5 rounded font-bold uppercase"
                        style={{ backgroundColor: `${LOG_TYPE_COLORS[log.type] || "#64748B"}15`, color: LOG_TYPE_COLORS[log.type] || "#64748B" }}>
                        {LOG_TYPE_LABELS[log.type] || log.type}
                      </span>
                      {log.ip_address && <span className="text-[10px] text-[#64748B] font-mono">{log.ip_address}</span>}
                      {log.user_id && <span className="text-[10px] text-[#64748B] font-mono">UID: {log.user_id.slice(-6)}</span>}
                    </div>
                    <p className="text-xs text-[#94A3B8] mt-1">{log.details}</p>
                    <p className="text-[10px] text-[#475569] mt-1">{new Date(log.created_at).toLocaleString()}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
